import * as React from 'react';
import { useTelemetry } from '@snowflake/core-ui';
import {
  ABExperimentName,
  DEFAULT_GROUP,
  ExistingAccountSummary,
  FeatureFlag,
  OidcProvider,
  SignupEventType,
} from '@signup/shared';
import { OidcSignupError, SignupApi } from '../../api/signupApi';
import {
  isFeatureEnabled,
  getExperimentGroup,
  setSignupFlowType,
} from '../../utils/configurations';
import { createRadarSessionForSignup } from '../../utils/stripeRadarSession';
import { DotsLoadingIndicator } from '../../components/Cards/shared/shared';
import { useLogOnClose } from '../../components/UseLogOnClose';
import { OIDC_FORM_ID_STORAGE_KEY, SignupEventName } from '../../utils/SignupLogger';
import {
  OIDC_SIGNUP_CONTEXT_KEY,
  OidcSignupContext,
  stringOrUndefined,
} from '../../components/SocialLoginButtons/SocialLoginButtons';
import { OidcAccountDetailsForm } from './OidcAccountDetailsForm';
import { OidcExistingAccountsPicker } from './OidcExistingAccountsPicker';

type OidcCallbackParams = {
  code: string;
  state: string;
  inFlightCtx: string;
  signupContext: OidcSignupContext;
};

type OidcState =
  | { phase: 'initializing' }
  | { phase: 'verifying' }
  | {
      phase: 'pickAccount';
      identityToken: string;
      email: string;
      existingAccounts: ExistingAccountSummary[];
      params: OidcCallbackParams;
    }
  | { phase: 'awaitingDetails'; identityToken: string; params: OidcCallbackParams }
  | { phase: 'success'; loginUrl: string }
  | { phase: 'error'; message: string; code?: string };

function getQueryParam(search: string, key: string): string | null {
  const params = new URLSearchParams(search);
  return params.get(key);
}

const ORIGINAL_SEARCH_MAX_LENGTH = 2048;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

// sessionStorage is user-editable and this value ends up in an <a href>, so it is only accepted
// when it is a same-origin relative path. Anything else is treated as absent (the caller falls
// back to '/'), never repaired.
function parseOriginalSearch(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length > ORIGINAL_SEARCH_MAX_LENGTH) return undefined;
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return undefined;
  // URL parsers strip tabs and newlines, so `/<TAB>/evil.com` would otherwise become `//evil.com`.
  if (CONTROL_CHARS.test(value)) return undefined;
  try {
    if (new URL(value, window.location.origin).origin !== window.location.origin) return undefined;
  } catch {
    return undefined;
  }
  return value;
}

export function readOidcSignupContext(): OidcSignupContext | null {
  const raw = sessionStorage.getItem(OIDC_SIGNUP_CONTEXT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<OidcSignupContext>;
    if (!parsed.edition || !parsed.cloud || !parsed.region || !parsed.country) {
      return null;
    }
    return {
      edition: parsed.edition,
      cloud: parsed.cloud,
      region: parsed.region,
      country: parsed.country,
      // Optional: absent for sessions started before this field existed. Passed through to the
      // complete request so the backend can restore the original signup flow (e.g. CortexCodeCLI).
      signupFlowType: parsed.signupFlowType,
      originalSearch: parseOriginalSearch(parsed.originalSearch),
      // Mechanical params mirrored from the regular signup flow (see SocialLoginButtons). All
      // optional and absent for sessions started before these fields existed.
      template: stringOrUndefined(parsed.template),
      lab: stringOrUndefined(parsed.lab),
      workspaces: stringOrUndefined(parsed.workspaces),
      account: stringOrUndefined(parsed.account),
      token: stringOrUndefined(parsed.token),
      E6CCEE4CEB: stringOrUndefined(parsed.E6CCEE4CEB),
      referrer: stringOrUndefined(parsed.referrer),
      owner: stringOrUndefined(parsed.owner),
      sdm: typeof parsed.sdm === 'boolean' ? parsed.sdm : undefined,
      utm_adgroup: stringOrUndefined(parsed.utm_adgroup),
      utm_source: stringOrUndefined(parsed.utm_source),
      utm_medium: stringOrUndefined(parsed.utm_medium),
      utm_campaign: stringOrUndefined(parsed.utm_campaign),
      utm_content: stringOrUndefined(parsed.utm_content),
      utm_term: stringOrUndefined(parsed.utm_term),
      utm_region: stringOrUndefined(parsed.utm_region),
      gclid: stringOrUndefined(parsed.gclid),
    };
  } catch {
    return null;
  }
}

function clearOidcSessionStorage(): void {
  sessionStorage.removeItem('oidcInFlightCtx');
  sessionStorage.removeItem(OIDC_FORM_ID_STORAGE_KEY);
  sessionStorage.removeItem('oidcProvider');
  sessionStorage.removeItem('oidcStartTime');
  sessionStorage.removeItem(OIDC_SIGNUP_CONTEXT_KEY);
}

function getOidcErrorMessage(code: string | undefined): string {
  switch (code) {
    case 'EMAIL_NOT_VERIFIED':
      return 'Your email address is not verified with your identity provider. Please verify it and try again.';
    case 'SIGNUP_BLOCKED':
      return 'We were unable to create an account for this email. Please return to sign up and try again, or contact support.';
    case 'INVALID_REGION':
      return 'The selected region is not available. Please return to sign up and choose a different region.';
    case 'OIDC_FLOW_TYPE_UNSUPPORTED':
      return "This signup link can't be used with Google or Microsoft sign-in. Please sign up with your email instead.";
    case 'LINK_INVALID':
      return 'This signup link is invalid or has expired. Please sign up with your email, or request a new link.';
    case 'INVALID_CTX':
    case 'MISSING_REQUIRED_FIELDS':
    case 'OIDC_VERIFICATION_FAILED':
    default:
      return 'Something went wrong creating your account. Please return to sign up and try again.';
  }
}

const REDIRECT_DELAY_MS = 1500;

interface OidcTelemetryContext {
  formId: string;
  provider: string;
  oidcExperimentGroup: string;
}

const OidcSuccessRedirect: React.FC<{
  loginUrl: string;
  telemetryContext: OidcTelemetryContext;
}> = ({ loginUrl, telemetryContext }) => {
  const { logEvent } = useTelemetry();

  React.useEffect(() => {
    logEvent({
      event: SignupEventName.OIDC_REDIRECT_TO_SNOWSIGHT,
      type: SignupEventType.UI_RESPONSE_SUCCESS,
      data: {
        ...telemetryContext,
        loginUrlHost: (() => {
          try {
            return new URL(loginUrl).host;
          } catch {
            return '';
          }
        })(),
      },
    });
    const timer = setTimeout(() => {
      window.location.href = loginUrl;
    }, REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [loginUrl]);

  return (
    <div className="oidc-success-card">
      <h2 className="oidc-success-title">Your Snowflake account is ready!</h2>
      <p className="oidc-success-subtitle">Redirecting you to Snowsight...</p>
      <DotsLoadingIndicator />
      <a href={loginUrl} className="oidc-success-link">
        Sign in to Snowflake
      </a>
    </div>
  );
};

const OidcErrorCard: React.FC<{ message: string; returnHref: string }> = ({
  message,
  returnHref,
}) => (
  <div className="oidc-error-card">
    <h2 className="oidc-error-title">Something went wrong</h2>
    <p className="oidc-error-message">{message}</p>
    <a href={returnHref} className="oidc-error-link">
      Return to sign up
    </a>
  </div>
);

const OidcLoadingCard: React.FC<{ text: string }> = ({ text }) => (
  <div className="oidc-loading-card">
    <DotsLoadingIndicator />
    <span className="oidc-loading-text">{text}</span>
  </div>
);

export const OidcCallbackPage: React.FC<{ formId: string }> = ({ formId }) => {
  const { logEvent } = useTelemetry();
  const [oidcState, setOidcState] = React.useState<OidcState>({ phase: 'initializing' });
  // Captured from the stored context on mount, before any clearOidcSessionStorage() call removes
  // it, so the error card can link back to the original signup URL (t, program, lab, utm_*, ...).
  const [originalSearch, setOriginalSearch] = React.useState<string | undefined>(undefined);

  // Telemetry context is stable across the page lifecycle. formId is passed down from
  // SignupRoot, which resolves it once via resolveFormId(pathname) -- the same call that seeds
  // the SignupLogger instance tagging every event on this page. Re-deriving it here from
  // sessionStorage independently (as this used to do) would disagree with the logger's value
  // whenever sessionStorage is empty: resolveFormId falls back to a fresh v4() in that case, so
  // a second independent call mints a *different* id than the one already in use, silently
  // breaking the client/server correlation this field exists for. Reusing the single
  // already-resolved value is what keeps telemetry and the server payload on the same id.
  const telemetryContext: OidcTelemetryContext = React.useMemo(() => {
    // Don't default to a specific provider — SocialLoginButtons always sets this before
    // redirecting, so a missing value means something genuinely went wrong (session cleared,
    // opened in a new tab, etc.). Defaulting to 'GOOGLE' would silently misattribute Microsoft
    // signups in analytics; a distinct 'UNKNOWN' sentinel surfaces the anomaly instead. This is
    // a telemetry tag only — the functional provider comes from the server-verified identity
    // JWT, so a missing tag here must not crash the callback page.
    const rawProvider = sessionStorage.getItem('oidcProvider');
    let oidcExperimentGroup: string = DEFAULT_GROUP;
    if (rawProvider === OidcProvider.GOOGLE) {
      oidcExperimentGroup = getExperimentGroup(ABExperimentName.OIDC_SIGNUP_GOOGLE);
    } else if (rawProvider === OidcProvider.MICROSOFT) {
      oidcExperimentGroup = getExperimentGroup(ABExperimentName.OIDC_SIGNUP_MICROSOFT);
    }
    return {
      formId,
      provider: rawProvider || 'UNKNOWN',
      oidcExperimentGroup,
    };
  }, [formId]);

  // Page-exit tracking for the two phases a user can get stuck on/abandon from: the
  // account-details form and the existing-accounts picker. Every other signup form in this
  // repo tracks this via useLogOnClose; this page previously didn't. startTime resets each
  // time we enter one of the two trackable phases so timeSpentOnStep reflects time on THAT
  // phase, not time on the whole callback page.
  const phaseStartTimeRef = React.useRef<number>(Date.now());
  React.useEffect(() => {
    if (oidcState.phase === 'awaitingDetails' || oidcState.phase === 'pickAccount') {
      phaseStartTimeRef.current = Date.now();
    }
  }, [oidcState.phase]);

  // Guards the verification effect below against running more than once for this component
  // instance (e.g. a StrictMode double-invoke). The authorization code and inFlightCtx token are
  // single-use, so a second call with the same values always fails server-side — this must be a
  // synchronous, render-independent latch, not state, or a second invocation before the first
  // setState commits would slip through.
  const hasStartedVerificationRef = React.useRef(false);

  const exitEventForPhase =
    oidcState.phase === 'awaitingDetails'
      ? SignupEventName.OIDC_EXIT_ACCOUNT_DETAILS_FORM
      : oidcState.phase === 'pickAccount'
        ? SignupEventName.OIDC_EXIT_EXISTING_ACCOUNTS_PICKER
        : '';

  useLogOnClose(
    { message: exitEventForPhase, startTime: phaseStartTimeRef.current / 1000 },
    telemetryContext,
  );

  // Validate the IdP redirect parameters and the cached signup context exactly
  // once when the page mounts. We no longer auto-submit — instead, we surface
  // the OidcAccountDetailsForm to collect company name and job title. The
  // actual /complete-oidc-signup call is fired from the form's submit handler.
  React.useEffect(() => {
    if (hasStartedVerificationRef.current) return;
    hasStartedVerificationRef.current = true;

    if (!isFeatureEnabled(FeatureFlag.FF_ENABLE_OIDC_SIGNUP)) {
      logEvent({
        event: SignupEventName.OIDC_CALLBACK_FEATURE_DISABLED,
        type: SignupEventType.UI_RESPONSE_ERROR,
        data: telemetryContext,
      });
      setOidcState({ phase: 'error', message: 'OIDC signup is not enabled.' });
      return;
    }

    const code = getQueryParam(window.location.search, 'code');
    const state = getQueryParam(window.location.search, 'state');
    const inFlightCtx = sessionStorage.getItem('oidcInFlightCtx');
    const signupContext = readOidcSignupContext();
    setOriginalSearch(signupContext?.originalSearch);

    // This page is a fresh server render reached via the IdP redirect, so `window.trial.signupFlowType`
    // resolves to Unknown (there's no case for /oidc/callback in determineUiSignupFlowType) -- restore
    // the real value now, before anything on this page reads it. Every `signup-flow-type` header sent
    // from here on (telemetry, verifyOidcIdentity, and the eventual complete call) reflects the flow
    // captured before the redirect instead of Unknown.
    if (signupContext?.signupFlowType) {
      setSignupFlowType(signupContext.signupFlowType);
    }

    // The authorization code and state are single-use -- GS consumes the code (and inFlightCtx)
    // on the first verifyIdentity call, so a reload, back/forward navigation, or restored tab
    // that re-sends them always fails, no matter how long ago the original attempt succeeded or
    // failed. Strip them from the address bar now, once they're captured into local variables, so
    // a reload of this page lands on the "missing OAuth parameters" branch below instead of
    // resubmitting an already-spent code to the server.
    if (code || state) {
      window.history.replaceState(null, '', window.location.pathname);
    }

    if (!code || !state || !inFlightCtx) {
      logEvent({
        event: SignupEventName.OIDC_CALLBACK_MISSING_PARAMS,
        type: SignupEventType.UI_RESPONSE_ERROR,
        data: {
          ...telemetryContext,
          missingCode: !code,
          missingState: !state,
          missingInFlightCtx: !inFlightCtx,
        },
      });
      setOidcState({
        phase: 'error',
        message: 'Missing OAuth parameters. Please try signing up again.',
      });
      return;
    }

    if (!signupContext) {
      logEvent({
        event: SignupEventName.OIDC_CALLBACK_MISSING_PARAMS,
        type: SignupEventType.UI_RESPONSE_ERROR,
        data: {
          ...telemetryContext,
          missingSignupContext: true,
        },
      });
      setOidcState({
        phase: 'error',
        message:
          'Your signup session has expired. Please return to the sign-up page and try again.',
      });
      return;
    }

    // Compute time spent at IdP to infer auto-approve vs manual interaction
    const startTimeStr = sessionStorage.getItem('oidcStartTime');
    const idpDurationMs = startTimeStr ? Date.now() - parseInt(startTimeStr, 10) : null;

    logEvent({
      event: SignupEventName.OIDC_CALLBACK_RECEIVED,
      type: SignupEventType.UI_RESPONSE_SUCCESS,
      data: {
        ...telemetryContext,
        idpDurationMs,
      },
    });

    setOidcState({ phase: 'verifying' });

    SignupApi.verifyOidcIdentity({
      code,
      state,
      inFlightCtx,
      region: signupContext.region,
      edition: signupContext.edition,
      cloud: signupContext.cloud,
      country: signupContext.country,
      // The pre-redirect formId restored from sessionStorage (see telemetryContext above), not a
      // fresh id -- this is what lets the server join this request to the earlier oidcStartSignup
      // call and to the completeOidcSignup call that follows it.
      formId: telemetryContext.formId,
    })
      .then(result => {
        if (result.existingAccounts.length > 0) {
          logEvent({
            event: SignupEventName.OIDC_EXISTING_ACCOUNTS_FOUND,
            type: SignupEventType.UI_RESPONSE_SUCCESS,
            data: {
              ...telemetryContext,
              existingAccountsCount: result.existingAccounts.length,
              idpDurationMs,
            },
          });
          setOidcState({
            phase: 'pickAccount',
            identityToken: result.identityToken,
            email: result.email,
            existingAccounts: result.existingAccounts,
            params: { code, state, inFlightCtx, signupContext },
          });
        } else {
          logEvent({
            event: SignupEventName.OIDC_ACCOUNT_DETAILS_SHOWN,
            type: SignupEventType.UI_RESPONSE_SUCCESS,
            data: {
              ...telemetryContext,
              region: signupContext.region,
              edition: signupContext.edition,
              cloud: signupContext.cloud,
            },
          });
          setOidcState({
            phase: 'awaitingDetails',
            identityToken: result.identityToken,
            params: { code, state, inFlightCtx, signupContext },
          });
        }
      })
      .catch(err => {
        logEvent({
          event: SignupEventName.OIDC_SIGNUP_ERROR,
          type: SignupEventType.UI_RESPONSE_ERROR,
          data: {
            ...telemetryContext,
            error: err instanceof Error ? err.message : String(err),
          },
        });

        if (err instanceof OidcSignupError) {
          clearOidcSessionStorage();
          setOidcState({ phase: 'error', message: getOidcErrorMessage(err.code), code: err.code });
          return;
        }
        setOidcState({
          phase: 'error',
          message:
            'Something went wrong verifying your identity. Please return to sign up and try again.',
        });
      });
    // telemetryContext is memoized with empty deps and logEvent is hook-stable,
    // so this effect runs exactly once on mount.
  }, [telemetryContext, logEvent]);

  const handlePickExisting = React.useCallback(
    (account: ExistingAccountSummary) => {
      logEvent({
        event: SignupEventName.OIDC_EXISTING_ACCOUNT_SELECTED,
        type: SignupEventType.UI_CLICK,
        data: {
          ...telemetryContext,
          accountLocator: account.accountLocator,
          accountCount:
            oidcState.phase === 'pickAccount' ? oidcState.existingAccounts.length : undefined,
        },
      });
      window.location.href = account.loginUrl;
    },
    [oidcState, logEvent, telemetryContext],
  );

  const handleCreateNew = React.useCallback(() => {
    if (oidcState.phase !== 'pickAccount') return;
    const { identityToken, params, existingAccounts } = oidcState;
    logEvent({
      event: SignupEventName.OIDC_CREATE_NEW_FROM_PICKER,
      type: SignupEventType.UI_CLICK,
      data: {
        ...telemetryContext,
        accountCount: existingAccounts.length,
      },
    });
    logEvent({
      event: SignupEventName.OIDC_ACCOUNT_DETAILS_SHOWN,
      type: SignupEventType.UI_RESPONSE_SUCCESS,
      data: {
        ...telemetryContext,
        region: params.signupContext.region,
        edition: params.signupContext.edition,
        cloud: params.signupContext.cloud,
      },
    });
    setOidcState({ phase: 'awaitingDetails', identityToken, params });
  }, [oidcState, logEvent, telemetryContext]);

  const handleDetailsSubmit = React.useCallback(
    async ({ company, role }: { company: string; role: string }) => {
      // We only ever transition to 'awaitingDetails' with a valid identityToken,
      // so this guard is only here to keep TypeScript happy.
      if (oidcState.phase !== 'awaitingDetails') return;
      const { identityToken, params } = oidcState;
      const { signupContext } = params;

      const submitStartTime = Date.now();

      logEvent({
        event: SignupEventName.OIDC_ACCOUNT_DETAILS_SUBMITTED,
        type: SignupEventType.UI_RESPONSE_SUCCESS,
        data: {
          ...telemetryContext,
          region: signupContext.region,
          edition: signupContext.edition,
          cloud: signupContext.cloud,
          autoSubmitted: false,
        },
      });

      // We deliberately do NOT transition to a "submitting" phase here — that
      // would unmount the form and lose the user's typed values on retry.
      // The form manages its own submit-pending state (button label + disabled).
      try {
        const radarSession = await createRadarSessionForSignup();
        const result = await SignupApi.completeOidcSignup({
          identityToken,
          edition: signupContext.edition,
          cloud: signupContext.cloud,
          region: signupContext.region,
          country: signupContext.country,
          company,
          role,
          signupFlowType: signupContext.signupFlowType,
          // Same restored formId sent on verifyOidcIdentity above, so this session's verify and
          // complete audit rows correlate.
          formId: telemetryContext.formId,
          ...(radarSession ? { radarSession } : {}),
          // Mechanical params mirrored from the regular signup flow, restored from the
          // pre-redirect signup context (see readOidcSignupContext above).
          template: signupContext.template,
          lab: signupContext.lab,
          workspaces: signupContext.workspaces,
          account: signupContext.account,
          token: signupContext.token,
          E6CCEE4CEB: signupContext.E6CCEE4CEB,
          referrer: signupContext.referrer,
          owner: signupContext.owner,
          sdm: signupContext.sdm,
          utm_adgroup: signupContext.utm_adgroup,
          utm_source: signupContext.utm_source,
          utm_medium: signupContext.utm_medium,
          utm_campaign: signupContext.utm_campaign,
          utm_content: signupContext.utm_content,
          utm_term: signupContext.utm_term,
          utm_region: signupContext.utm_region,
          gclid: signupContext.gclid,
        });
        clearOidcSessionStorage();

        logEvent({
          event: SignupEventName.OIDC_SIGNUP_SUCCESS,
          type: SignupEventType.UI_RESPONSE_SUCCESS,
          data: {
            ...telemetryContext,
            region: signupContext.region,
            edition: signupContext.edition,
            cloud: signupContext.cloud,
            locator: result.locator,
            completeApiLatencyMs: Date.now() - submitStartTime,
          },
        });

        setOidcState({ phase: 'success', loginUrl: result.loginUrl });
      } catch (err) {
        logEvent({
          event: SignupEventName.OIDC_SIGNUP_ERROR,
          type: SignupEventType.UI_RESPONSE_ERROR,
          data: {
            ...telemetryContext,
            region: signupContext.region,
            edition: signupContext.edition,
            cloud: signupContext.cloud,
            error: err instanceof Error ? err.message : String(err),
            completeApiLatencyMs: Date.now() - submitStartTime,
          },
        });

        // Server-side rejections are non-recoverable: the identity token has been verified
        // but account creation failed. Route these to the error card so the user has a
        // clear path back to the signup page.
        //
        // Transport errors (network failure, timeout) DO make sense to retry in-form —
        // re-throw so the form surfaces an inline error and re-enables the submit button.
        if (err instanceof OidcSignupError) {
          clearOidcSessionStorage();
          setOidcState({ phase: 'error', message: getOidcErrorMessage(err.code), code: err.code });
          return;
        }
        throw err;
      }
    },
    [oidcState, logEvent, telemetryContext],
  );

  const renderContent = () => {
    if (oidcState.phase === 'initializing' || oidcState.phase === 'verifying') {
      return <OidcLoadingCard text="Verifying your identity..." />;
    }
    if (oidcState.phase === 'pickAccount') {
      return (
        <OidcExistingAccountsPicker
          email={oidcState.email}
          existingAccounts={oidcState.existingAccounts}
          onSelectExisting={handlePickExisting}
          onCreateNew={handleCreateNew}
        />
      );
    }
    if (oidcState.phase === 'awaitingDetails') {
      return <OidcAccountDetailsForm onSubmit={handleDetailsSubmit} />;
    }
    if (oidcState.phase === 'error') {
      // LINK_INVALID tells the user to request a new link, so sending them back to the invalid
      // one would be a dead end.
      const returnHref = oidcState.code === 'LINK_INVALID' ? '/' : (originalSearch ?? '/');
      return <OidcErrorCard message={oidcState.message} returnHref={returnHref} />;
    }
    return (
      <OidcSuccessRedirect loginUrl={oidcState.loginUrl} telemetryContext={telemetryContext} />
    );
  };

  return (
    <div className="oidc-page-container">
      <div className="oidc-page-header">
        <a href="https://www.snowflake.com">
          <img src="/static/images/logo-sno-blue.svg" alt="Snowflake" />
        </a>
      </div>
      <div className="oidc-page-content">
        <div className="oidc-form-card">{renderContent()}</div>
      </div>
    </div>
  );
};
